import { Form, Modal, Space, Spin, Table, Tooltip, notification } from "antd";
import React, { useState } from "react";
import { EmployeeService } from "../../../../services/employee/employee.service";
import { InsertEmployeeForm } from "./insert-form";
import { Button } from "react-bootstrap";
import { eApproveTitleCode, eBizTitleOptions, eLocation } from "../../../../constants/bizTitle";
import { CreateNotification } from "../../../../components/notifier";
import { HiOutlineTrash, HiPencilSquare } from "react-icons/hi2";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import UploadExcelFile from "../../../../components/input/arrayData/upload-excel-file";
import DownloadExcelTemplate from "../../../../components/input/arrayData/DownloadExcelTemplate";
import { departmentList } from "../../../../constants";
import { IEmployee } from "../../../../models";
import { ColumnsType } from "antd/lib/table";
import { useColumn } from "./hooks/use-column";
import { usePermissions } from "../../../role-control/hooks/use-permissions";
import { useCheckPermission } from "../Permission/hooks/use-check-permission";
import { configPermission } from "../../../../constants/configPermission";

const ConfigEmployees = () => {
  const [form] = Form.useForm();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null as any);
  const queryClient = useQueryClient();
  const { getColumnSearchProps } = useColumn();
  const isUpdate = useCheckPermission(configPermission.UPDATE);
  const isDelete = useCheckPermission(configPermission.DELETE);
  const isCreate = useCheckPermission(configPermission.CREATE);

  const onCloseModal = () => {
    setSelectedItem(null);
    setIsModalOpen(false);
  };

  const onEdit = (record: any) => {
    const course = employees.data?.find((e: any) => e.eMail === record.eMail);
    setSelectedItem(course);
    setTimeout(() => setIsModalOpen(true), 0);
  };

  const showDeleteConfirm = (id: string) => {
    Modal.confirm({
      title: "Thông báo",
      content: "Anh/chị vui lòng xác nhận xoá dữ liệu đã chọn!",
      okText: "Xác nhận",
      okType: "danger",
      okButtonProps: { type: "primary" },
      cancelText: "Huỷ",
      onOk() {
        onRemove(id);
      },
    });
  };

  const onRemove = async (id: string) => {
    try {
      await EmployeeService.deleteEmployee(id);
      CreateNotification({
        title: "Thành công",
        body: "Xoá thông tin nhân viên thành công",
        icon: "/icons/success.png",
      });
      queryClient.invalidateQueries({ queryKey: ["employee"] });
    } catch (error) {}
  };

  const onSubmit = (values: any) => {
    let eBizTitle = eBizTitleOptions.find(
      (e: { label: string; value: string }) => e.value === values.eBizTitleCode,
    )?.label;
    let eBizTitleCode = values.eBizTitleCode;

    let eDepartment = departmentList.find((e: any) => e.value === values.eDepartmentCode)?.label;
    let eDepartmentCode = values.eDepartmentCode;
    handleAdd({ ...values, eBizTitle: eBizTitle || eBizTitleCode, eDepartment: eDepartment || eDepartmentCode });
    onCloseModal();
  };

  const getEmployees = async () => {
    try {
      return await EmployeeService.getEmployees();
    } catch (error) {
      console.log(error);
    }
  };

  const columns: ColumnsType<any> = [
    {
      key: "action",
      dataIndex: "action",
      width: 30,
      align: "left" as const,
      render: (_: string, record: any) => (
        <Space>
          {isUpdate && (
            <Tooltip title="Chỉnh sửa">
              <HiPencilSquare size={20} className="cursor-pointer" color="#0ea5e9" onClick={() => onEdit(record)} />
            </Tooltip>
          )}
          {isDelete && (
            <Tooltip title="Xoá">
              <HiOutlineTrash
                size={20}
                className="cursor-pointer"
                color="#dc2626"
                onClick={() => showDeleteConfirm(record.id)}
              />
            </Tooltip>
          )}
        </Space>
      ),
    },
    {
      title: "Mã NV",
      dataIndex: "eEmployeeId",
      sorter: (a: any, b: any) => a.eEmployeeId - b.eEmployeeId,
      sortDirections: ["descend", "ascend"],
      showSorterTooltip: { title: "Click vào để sắp xếp" },
    },
    {
      title: "Họ tên",
      dataIndex: "Title",
      ...getColumnSearchProps("Title", "Nhập tên cần người cần tìm"),
    },
    {
      title: "Phòng ban",
      dataIndex: "eDepartment",
      ...getColumnSearchProps("eDepartment", "Nhập tên phòng ban cần tìm"),
    },
    {
      title: "Email",
      dataIndex: "eMail",
      ...getColumnSearchProps("eMail", "Nhập email cần tìm"),
    },
    {
      title: "Chức vụ",
      dataIndex: "eBizTitle",
    },
    {
      title: "Vị trí",
      dataIndex: "eLocation",
    },
    {
      title: "Số điện thoại",
      dataIndex: "ePhone",
    },
  ];

  const handleAdd = async (newData: any) => {
    try {
      let res;
      if (selectedItem) {
        const { id, key, "@odata.etag": _tag, ...newFields } = newData;
        const updateFields = {
          ...newFields,
          eDefault: eApproveTitleCode.includes(newFields.eBizTitleCode),
        };
        res = await EmployeeService.updateEmployee(selectedItem.id, updateFields);
        if (res.status === 200) {
          CreateNotification({
            title: "Thành công",
            body: "Cập nhật thông tin nhân viên mới thành công",
            icon: "/icons/success.png",
          });
          queryClient.invalidateQueries({ queryKey: ["employee"] });
        } else
          CreateNotification({
            title: "Không thể cập nhật",
            body: "Không thể cập nhật nhân viên, vui lòng thử lại",
            icon: "/icons/warning.png",
          });
      } else {
        const { key, ...newDataWithoutKey } = newData;
        const insertParams = {
          fields: {
            ...newDataWithoutKey,
            eDefault: eApproveTitleCode.indexOf(newDataWithoutKey.eBizTitleCode) > -1,
          },
        };
        res = await EmployeeService.addEmployee(insertParams);
        if (res.status === 201) {
          CreateNotification({
            title: "Thành công",
            body: "Thêm nhân viên mới thành công",
            icon: "/icons/success.png",
          });
          queryClient.invalidateQueries({ queryKey: ["employee"] });
        } else
          CreateNotification({
            title: "Không thể thêm",
            body: "Không thể thêm nhân viên, vui lòng thử lại",
            icon: "/icons/warning.png",
          });
      }
    } catch (error) {}
  };

  const employees = useQuery({
    queryKey: ["employee"],
    queryFn: getEmployees,
  });

  const onImportFileChange = async (values: any[] = []) => {
    const getCodeByLabel = (options: { label: string; value: string; key: number | string }[], label: string) =>
      options.find((e: any) => e.label === label)?.value || "";

    const insertValues = values.map((e: IEmployee) => {
      return {
        ...e,
        eDepartmentCode: getCodeByLabel(departmentList, e.eDepartment),
        eBizTitleCode: getCodeByLabel(eBizTitleOptions, e.eBizTitle),
        eLocation: getCodeByLabel(eLocation, e.eLocation),
        eDefault: eApproveTitleCode.includes(getCodeByLabel(eBizTitleOptions, e.eBizTitle)),
      };
    });
    try {
      const promises: any[] = [];

      insertValues.forEach((value: IEmployee) => {
        const insertParams = {
          fields: {
            ...value,
          },
        };
        promises.push(EmployeeService.addEmployee(insertParams));
      });

      await Promise.all(promises);
      queryClient.invalidateQueries({ queryKey: ["employee"] });
      notification.success({
        message: "Thành công",
        description: "Nhập liệu thành công!",
      });
    } catch (error) {
      notification.error({
        message: "Thất bại",
        description: "Nhập liệu thất bại. Vui lòng kiểm tra lại định dạng dữ liệu",
      });
    }
  };

  return (
    <div>
      <Spin spinning={employees.isLoading}>
        <Form form={form} component={false}>
          {isCreate && (
            <>
              <Button style={{ margin: "10px", marginLeft: 0 }} variant="primary" onClick={() => setIsModalOpen(true)}>
                Thêm mới
              </Button>
              <UploadExcelFile template="config-employee" onChange={onImportFileChange}>
                <Button variant="success">Nhập liệu</Button>
              </UploadExcelFile>
              <DownloadExcelTemplate template="config-employee" className="download-excel-btn" />
            </>
          )}
          <Form.Item>
            <Table
              bordered
              dataSource={employees.data}
              columns={columns}
              rowClassName="editable-row"
              style={{ overflowX: "auto" }}
              scroll={{ x: "1200px" }}
            />
          </Form.Item>
          <Modal
            width={1000}
            title={<div style={{ fontSize: 18 }}>{selectedItem ? "Chỉnh sửa nhân viên" : "Thêm nhân viên mới"}</div>}
            open={isModalOpen}
            footer={null}
            onCancel={onCloseModal}
            destroyOnClose
          >
            <InsertEmployeeForm onSubmit={onSubmit} employee={selectedItem} department={departmentList} />
          </Modal>
        </Form>
      </Spin>
    </div>
  );
};

export default ConfigEmployees;
